import { useContext, useMemo, useState } from 'react';
import { Checkbox, InputAdornment, TextField, Theme, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { permissionLabel } from '@terragotech/gen5-shared-utilities';
import { chain, toLower } from 'lodash';
import { Editor2Props } from '@terragotech/react-data-grid';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import { useUserInfo } from '@terragotech/gen5-shared-components';
import { TableData } from '../../hooks/useTable';
import { User, UserWithoutId } from './types';
import useStyles, { getClasses } from '../../components/StyledTable/Editors/Common';
import PermissionModal from './PermissionModal';
import { colors } from '../../styles/theme';
import { LanguageContext } from '../../contexts/LanguageContext/languageContext';
import { useRecoilValue } from 'recoil';
import { configState } from '../../recoil/atoms';

export interface AdminPermissionEditorModalProp extends Editor2Props<TableData> {
  numberOfUsersWithAnalyticsPermissions: number;
}

const AdminPermissionsEditorModal = (props: AdminPermissionEditorModalProp) => {
  const { row, onRowChange, onClose, numberOfUsersWithAnalyticsPermissions } = props;
  const classes = useLocalStyles();
  const { translate } = useContext(LanguageContext);
  const tableClasses = useStyles();
  const { isTgoAdmin } = useUserInfo();
  const uiConfig = useRecoilValue(configState);

  const analyticsEnabled = useMemo(() => uiConfig.enabledFeatures?.analyticsModule, [uiConfig]);
  const analyticsMaxSeatCount = useMemo(() => uiConfig.analyticsMaxSeatCount, [uiConfig]);
  const mapServicesEnabled = useMemo(() => uiConfig.integrations?.mapServices, [uiConfig]);
  const floorPlanEnabled = useMemo(() => uiConfig.integrations?.floorPlans, [uiConfig]);
  const [currentPermission, setCurrentPermission] = useState('');

  const canModifyAnalyticsStatus = isTgoAdmin;
  const canModifyMapServicesPermissions = isTgoAdmin;
  const canModifyFloorPlanPermissions = isTgoAdmin;
  const canAccessAdvancedLogging = isTgoAdmin;
  const canAccessDataExportTracking = isTgoAdmin;
  const canAccessAI = isTgoAdmin;
  const canAccessSystemLogging = isTgoAdmin;

  const [isModalOpen, setIsModalOpen] = useState(false);
  //I don't like these casts, but nested objects don't work well with ValueType
  const [isUserAdmin, setIsUserAdmin] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.isUserAdmin
  );
  const [isRoleAndDataAdmin, setIsRoleAndDataAdmin] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.isRoleAndDataAdmin
  );
  const [isAnalyticsUser, setIsAnalyticsUser] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.isAnalyticsUser
  );
  const [isMapServicesUser, setIsMapServicesUser] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.isMapServicesUser
  );
  const [isFloorPlanUser, setIsFloorPlanUser] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.isFloorPlanUser
  );
  const [isAdvancedLoggingUser, setIsAdvancedLoggingUser] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.hasAdvancedLoggingAccess
  );
  const [isDataExportTrackingUser, setIsDataExportTrackingUser] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.hasDataExportTrackingAccess
  );
  const [isSystemLoggingUser, setIsSystemLoggingUser] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.hasSystemLoggingAccess
  );
  const [isAIUser, setIsAIUser] = useState(
    (row.adminPermissions as UserWithoutId['adminPermissions'])?.hasAiAnalysisAccess
  );

  const [currentAnalyticsSeatCount, setCurrentAnalyticsSeatCount] = useState(numberOfUsersWithAnalyticsPermissions);
  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleOnClick = () => {
    setIsModalOpen(true);
  };
  const handleOkClicked = () => {
    const oldPerms = row.adminPermissions as UserWithoutId['adminPermissions'];
    const newPerms = {
      isUserAdmin: !!isUserAdmin,
      isRoleAndDataAdmin: !!isRoleAndDataAdmin,
      isAnalyticsUser: !!isAnalyticsUser,
      isMapServicesUser: !!isMapServicesUser,
      isFloorPlanUser: !!isFloorPlanUser,
      hasAdvancedLoggingAccess: !!isAdvancedLoggingUser,
      hasDataExportTrackingAccess: !!isDataExportTrackingUser,
      hasSystemLoggingAccess: !!isSystemLoggingUser,
      hasAiAnalysisAccess: !!isAIUser,
    };

    const changed =
      !!oldPerms?.isUserAdmin !== newPerms.isUserAdmin ||
      !!oldPerms?.isRoleAndDataAdmin !== newPerms.isRoleAndDataAdmin ||
      !!oldPerms?.isAnalyticsUser !== newPerms.isAnalyticsUser ||
      !!oldPerms?.isMapServicesUser !== newPerms.isMapServicesUser ||
      !!oldPerms?.isFloorPlanUser !== newPerms.isFloorPlanUser ||
      !!oldPerms?.hasAdvancedLoggingAccess !== newPerms.hasAdvancedLoggingAccess ||
      !!oldPerms?.hasDataExportTrackingAccess !== newPerms.hasDataExportTrackingAccess ||
      !!oldPerms?.hasSystemLoggingAccess !== newPerms.hasSystemLoggingAccess ||
      !!oldPerms?.hasAiAnalysisAccess !== newPerms.hasAiAnalysisAccess;

    if (changed) {
      onRowChange(
        {
          ...row,
          adminPermissions: newPerms,
          __changes: {
            adminPermissions: newPerms,
          },
        },
        true
      );
    }

    handleClose();
  };
  const handleCancelClicked = () => {
    handleClose();
    onClose(false);
  };

  const handleFilterSearch = (value: string) => {
    setCurrentPermission(value);
  };

  const permissions = useMemo(() => {
    return chain([
      {
        key: 'user',
        checked: isUserAdmin,
        onChange: (value?: boolean) => setIsUserAdmin(typeof value === 'boolean' ? value : !isUserAdmin),
        title: 'Manage users',
        description:
          'This includes permissions to view and edit all users on the server. Editing permissions include the users name, email, phone number, assigned roles, and admin permissions.',
      },
      {
        key: 'role',
        checked: isRoleAndDataAdmin,
        onChange: (value?: boolean) => setIsRoleAndDataAdmin(typeof value === 'boolean' ? value : !isRoleAndDataAdmin),
        title: 'Manage roles and all server data',
        description:
          'This includes permissions to view and edit all user roles on the server. This also includes the permissions to see all data on the server including all record types, folders, and projects.',
      },
      canModifyMapServicesPermissions &&
        mapServicesEnabled && {
          key: 'mapService',
          checked: isMapServicesUser,
          onChange: (value?: boolean) => setIsMapServicesUser(typeof value === 'boolean' ? value : !isMapServicesUser),
          title: 'Manage Map Services',
          description: 'This includes permissions to add Map Services, including external map layers.',
        },
      canModifyFloorPlanPermissions &&
        floorPlanEnabled && {
          key: 'floorPlan',
          checked: isFloorPlanUser,
          onChange: (value?: boolean) => setIsFloorPlanUser(typeof value === 'boolean' ? value : !isFloorPlanUser),
          title: 'Manage Floor Plans',
          description: 'This includes permissions to add Floor Plans and Buildings.',
        },
      canModifyAnalyticsStatus &&
        analyticsEnabled && {
          key: 'analytics',
          checked: isAnalyticsUser,
          onChange: (value?: boolean) => {
            setCurrentAnalyticsSeatCount(
              isAnalyticsUser ? currentAnalyticsSeatCount - 1 : currentAnalyticsSeatCount + 1
            );
            setIsAnalyticsUser(typeof value === 'boolean' ? value : !isAnalyticsUser);
          },
          title: 'Access Analytics',
          description: 'This includes permissions to view the analytics module with all currently active dashboards.',
        },
      canAccessAdvancedLogging && {
        key: 'advancedLogging',
        checked: isAdvancedLoggingUser,
        onChange: (value?: boolean) =>
          setIsAdvancedLoggingUser(typeof value === 'boolean' ? value : !isAdvancedLoggingUser),
        title: 'Access API Logging',
        description: 'This includes permissions to view and edit API logging settings.',
      },
      canAccessDataExportTracking && {
        key: 'dataExportTracking',
        checked: isDataExportTrackingUser,
        onChange: (value?: boolean) =>
          setIsDataExportTrackingUser(typeof value === 'boolean' ? value : !isDataExportTrackingUser),
        title: 'Access Data Export Tracking',
        description: 'This includes permissions to view and edit data export tracking settings.',
      },
      canAccessSystemLogging && {
        key: 'systemLogging',
        checked: isSystemLoggingUser,
        onChange: (value?: boolean) =>
          setIsSystemLoggingUser(typeof value === 'boolean' ? value : !isSystemLoggingUser),
        title: 'Access System Logging',
        description: 'This includes permissions to view and edit system logging settings.',
      },
      canAccessAI && {
        key: 'aiAnalytics',
        checked: isAIUser,
        onChange: (value?: boolean) => setIsAIUser(typeof value === 'boolean' ? value : !isAIUser),
        title: 'Access AI Chat',
        description: 'This includes permissions to view and interact with our AI analysis bot.',
      },
    ])
      .compact()
      .filter(value => toLower(value.title).includes(toLower(currentPermission)))
      .value();
  }, [
    isUserAdmin,
    isRoleAndDataAdmin,
    isMapServicesUser,
    isFloorPlanUser,
    isAnalyticsUser,
    currentAnalyticsSeatCount,
    analyticsEnabled,
    canModifyAnalyticsStatus,
    mapServicesEnabled,
    canModifyMapServicesPermissions,
    floorPlanEnabled,
    canModifyFloorPlanPermissions,
    currentPermission,
    isAdvancedLoggingUser,
    canAccessAdvancedLogging,
    isDataExportTrackingUser,
    canAccessDataExportTracking,
    isSystemLoggingUser,
    isAIUser,
    canAccessSystemLogging,
  ]);

  const isAllSelected = permissions.every(x => x.checked);

  const toggleSelection = () => {
    permissions.forEach(p => p.onChange(!isAllSelected));
  };

  return (
    <div className={getClasses(props, tableClasses).join(' ')}>
      <TextField
        value={permissionLabel((row as User).adminPermissions).join(', ')}
        /*onClick={handleOnClick}*/
        inputProps={{ 'aria-label': `change data`, className: classes.input }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDown color="primary" className={classes.icon} />
            </InputAdornment>
          ),
          disableUnderline: true,
        }}
        onClick={handleOnClick}
      />
      <PermissionModal
        {...{
          open: isModalOpen,
          handleClose,
          displayName: translate('Permissions'),
          handleFilterSearch,
          handleApply: handleOkClicked,
          handleCancel: handleCancelClicked,
        }}>
        <Box className={classes.content}>
          <Typography className={classes.headerDescription}>
            {translate('Configure specific permissions for this user.')}
          </Typography>
          <Typography className={classes.toggleButton} onClick={toggleSelection}>
            {`${translate(isAllSelected ? 'Deselect All' : 'Select All')} (${permissions.length})`}
          </Typography>
          {permissions.map(permission => {
            return (
              <Box className={classes.permissionContainer} key={permission.key}>
                <Checkbox
                  className={classes.checkbox}
                  checked={Boolean(permission.checked)}
                  onChange={() => permission.onChange()}
                  color={'primary'}
                  disableRipple
                />
                <Box className={classes.descriptionContainer}>
                  <Typography className={classes.titleText}>{translate(permission.title)}</Typography>
                  <Typography className={classes.descriptionText}>{translate(permission.description)}</Typography>
                  {permission.key === 'analytics' && (
                    <div
                      className={`${classes.license} 
                        ${
                          currentAnalyticsSeatCount < analyticsMaxSeatCount
                            ? classes.availableLicense
                            : classes.unavailableLicense
                        }
                      `}>
                      <>
                        {' '}
                        {translate('Licenses Available')}:{' '}
                        {`${analyticsMaxSeatCount - currentAnalyticsSeatCount} ${translate(
                          'of'
                        )} ${analyticsMaxSeatCount}.`}{' '}
                        {currentAnalyticsSeatCount >= analyticsMaxSeatCount && (
                          <span className={classes.normalFont}>
                            <>
                              {translate(
                                'Remove analytics access from other users or purchase additional licenses to enable access for this user.'
                              )}
                            </>
                          </span>
                        )}
                      </>
                    </div>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </PermissionModal>
    </div>
  );
};

const useLocalStyles = makeStyles((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  checkbox: {
    padding: 0,
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '23px',
    paddingBottom: '26px',
  },
  headerDescription: {
    color: colors.black54,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 'normal',
  },
  toggleButton: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.primary.main,
    lineHeight: 'normal',
    cursor: 'pointer',
  },
  permissionContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  descriptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  titleText: {
    color: colors.black0,
    fontSize: 15,
    lineHeight: 'normal',
    fontWeight: 400,
  },
  descriptionText: {
    color: colors.black54,
    fontSize: 14,
    lineHeight: 'normal',
    fontWeight: 400,
  },
  input: {
    fontWeight: 500,
    fontFamily: theme.typography.fontFamily,
    fontSize: '13px',
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
    padding: 0,
    verticalAlign: 'middle',
  },
  icon: {
    width: '15px',
    height: '15px',
  },
  license: {
    fontSize: '13px',
    lineHeight: '128.9%',
    fontWeight: 500,
  },
  availableLicense: {
    color: `${theme.palette.success.main}`,
  },
  unavailableLicense: {
    color: `${theme.palette.error.main}`,
  },
  normalFont: {
    fontWeight: 'normal',
  },
}));

export default AdminPermissionsEditorModal;
